import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Sprout, Handshake, HeartHandshake, GraduationCap } from "lucide-react";
import { IMAGES } from "../../lib/content";
import { Reveal } from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const PATHWAYS = [
  { key: "wahine", label: "Wāhine", icon: Sprout, blurb: "Register your interest to begin the journey." },
  { key: "partner", label: "Partners", icon: Handshake, blurb: "Iwi, educators & organisations walking with us." },
  { key: "supporter", label: "Supporters & Investors", icon: HeartHandshake, blurb: "Help plant the first seed." },
          { key: "mentor", label: "Suppliers & Contributors", icon: GraduationCap, blurb: "Māori & rongoā producers interested in becoming a stockist supplier." },
];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="overline text-cream/50 text-[0.65rem]">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-cream/25 py-3 text-cream placeholder:text-cream/30 font-light text-lg outline-none focus:border-gold transition-colors duration-300"
    />
  </label>
);

export const Connect = () => {
  const [pathway, setPathway] = useState("wahine");
  const [form, setForm] = useState({ name: "", email: "", phone: "", organisation: "", message: "", hp: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.hp) return; // honeypot
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest_type: pathway });
      toast.success("Ngā mihi nui. Your message has reached us — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", organisation: "", message: "", hp: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="connect" data-testid="connect-section" className="relative bg-forest text-cream overflow-hidden">
      {/* Cinematic banner */}
      <div className="relative h-[46vh] min-h-[360px] flex items-center justify-center overflow-hidden">
        <motion.img
          src={IMAGES.mistMountains}
          alt="Mist rising over the ranges of Aotearoa at dawn"
          initial={{ scale: 1.12 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-forest-deep/70" />
        <Reveal className="relative z-10 text-center px-6">
          <p className="overline text-gold mb-6">Whakapā Mai · Connect</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
            Begin the conversation.
          </h2>
        </Reveal>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        {/* Pathways */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream/10 border border-cream/10 mb-16">
          {PATHWAYS.map((p) => {
            const Icon = p.icon;
            const active = pathway === p.key;
            return (
              <button
                key={p.key}
                data-testid={`pathway-${p.key}`}
                onClick={() => setPathway(p.key)}
                className={`text-left p-7 transition-colors duration-300 ${
                  active ? "bg-gold text-forest" : "bg-forest hover:bg-forest-deep"
                }`}
              >
                <Icon strokeWidth={1.2} size={26} className={active ? "text-forest" : "text-gold"} />
                <h3 className="font-serif-display text-xl sm:text-2xl font-light mt-4">{p.label}</h3>
                <p className={`mt-2 font-light text-sm ${active ? "text-forest/80" : "text-cream/55"}`}>
                  {p.blurb}
                </p>
              </button>
            );
          })}
        </div>

        {/* Form */}
        <Reveal>
          <form onSubmit={submit} data-testid="lead-form" className="max-w-3xl mx-auto space-y-8">
            {/* honeypot (hidden from users) */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp}
              onChange={update("hp")}
              className="hidden"
              aria-hidden="true"
            />
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Your Name *" data-testid="lead-name" value={form.name} onChange={update("name")} placeholder="Full name" />
              <Field label="Email *" type="email" data-testid="lead-email" value={form.email} onChange={update("email")} placeholder="you@example.com" />
              <Field label="Phone" data-testid="lead-phone" value={form.phone} onChange={update("phone")} placeholder="Optional" />
              <Field label="Organisation" data-testid="lead-org" value={form.organisation} onChange={update("organisation")} placeholder="Optional" />
            </div>
            <label className="block">
              <span className="overline text-cream/50 text-[0.65rem]">Your Message</span>
              <textarea
                data-testid="lead-message"
                value={form.message}
                onChange={update("message")}
                rows={3}
                placeholder="Tell us what moves you…"
                className="mt-2 w-full bg-transparent border-b border-cream/25 py-3 text-cream placeholder:text-cream/30 font-light text-lg outline-none focus:border-gold transition-colors duration-300 resize-none"
              />
            </label>
            <button
              type="submit"
              data-testid="lead-submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-cream text-forest px-9 py-4 text-sm tracking-wide hover:bg-gold transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
